import React, { useEffect, useState } from "react";
import { Autocomplete, Button, Grid, Input, Paper, TextField, Toolbar, Tooltip, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import moment from "moment";

import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { BASE_URL } from "../../global";
import MultiValueInputModal from "../common/multiValueInputModal";

const calibrationTypeOptions = [{ id: 3, name: "Third Party" }];

const modalConfigs = [
  { buttonLabel: "Range", transformedStringLabel: "ranges" },
  { buttonLabel: "LC", transformedStringLabel: "lc" },
  // { buttonLabel: "Accuracy", transformedStringLabel: "accuracy" },
];

const EditExternalCalibration = () => {
  const [inputStates, setInputStates] = useState({});
  const [clientsArray, setClientsArray] = useState([]);
  const [addressesArray, setAddressesArray] = useState([]);
  const [instrumentsArray, setInstrumentsArray] = useState([]);
  const params = useParams();
  const editAccess = localStorage.getItem("editAccess")?.split(",").map(Number);

  const updateInputObject = (key, value) => {
    setInputStates((prevInputStates) => ({
      ...prevInputStates,
      [key]: value,
    }));
  };

  const refresh = () => {
    window.location.reload(false);
  };

  const handleSubmit = () => {
    // Mandatory field validation
    if (!inputStates.clientId) {
      toast.error("Please select Client Name!");
      return;
    }

    if (!inputStates.address) {
      toast.error("Please select Address!");
      return;
    }

    
    if (!inputStates.instrumentId) {
      toast.error("Please select Instrument Name!");
      return;
    }
    
    if (!inputStates.ducId?.trim() && !inputStates.serialNumber?.trim()) {
      toast.error("Please enter either DUC ID or Serial Number!");
      return;
    }

    if (!inputStates.calibrationDate) {
      toast.error("Please select Calibration Date!");
      return;
    }

    if(!inputStates.dueDate) {
      toast.error("Please select Due Date!");
      return;
    }

    if(!inputStates.certificateNumber) {
      toast.error("Please enter Certificate Number!");
      return;
    }
    const url = BASE_URL;
    const formattedInputStates = {
      ...inputStates,
      entryDate: inputStates.entryDate ? moment(inputStates.entryDate).format("YYYY-MM-DD") : null,
      calibrationDate: inputStates.calibrationDate ? moment(inputStates.calibrationDate).format("YYYY-MM-DD") : null,
      dueDate: inputStates.dueDate ? moment(inputStates.dueDate).format("YYYY-MM-DD") : null,
    };

    if (params.id > 0) {
      axiosWithToken
        .patch(url + `externalCalibration/${params.id}`, formattedInputStates)
        .then(() => {
          setTimeout(refresh, 500);
          toast("ExternalCalibration edit successfully !");
        })
        .catch(() => {
          toast.error("Something Went Wrong!");
        });
    } else {
      axiosWithToken
        .post(url + "externalCalibration", formattedInputStates)
        .then(() => {
          setTimeout(refresh, 500);
          toast("ExternalCalibration created successfully !");
        })
        .catch(() => {
          toast.error("Something Went Wrong!");
        });
    }
  };

  const fetchExternalCalibration = () => {
    axiosWithToken
      .get(BASE_URL + `externalCalibration/${params.id}`)
      .then((res) => {
        const dataObject = res.data[0];
        if (!dataObject) return;
        const { lastModified, ...restDataObject } = dataObject;
        setInputStates((prev) => ({ ...prev, ...restDataObject }));
      })
      .catch(() => {
        toast.error("Something Went Wrong!");
      });
  };

  const fetchClients = () => {
    const sqlQuery = {
      query: `SELECT * FROM clients WHERE status != -1 ORDER BY companyName ASC`,
    };
    axiosWithToken
     .post(BASE_URL + `dynamic`, sqlQuery)
      .then((res) => {
        setClientsArray((res.data || []).filter((item) => item.status === 1));
      })
      .catch(() => {
        toast.error("Something Went Wrong!");
      });
  };

  const fetchInstruments = () => {
    axiosWithToken
      .get(BASE_URL + "instruments?_fields=id,instrumentName&_where=(status,eq,1)")
      .then((res) => {
        setInstrumentsArray(res.data || []);
      })
      .catch(() => {
        toast.error("Something Went Wrong!");
      });
  };

  const fetchAddressesArray = (clientId) => {
    const SQLQuery = {
      query: `SELECT id, address FROM addresses WHERE FIND_IN_SET(id, (SELECT address FROM clients WHERE id = ${clientId}))`,
    };
    axiosWithToken
      .post(BASE_URL + `dynamic`, SQLQuery)
      .then((res) => {
        res.data && setAddressesArray(res.data);
      })
      .catch(() => {
        toast.error("Something Went Wrong!");
      });
  };

  const handleFileUpload = (event, stateKey) => {
    const file = event.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append("fileToUpload", file);
    axiosWithToken
      .post(BASE_URL + `fileUpload/fileUpload.php`, data)
      .then((res) => {
        if (res.status == 200) {
          updateInputObject(stateKey, res.data);
          toast.success("File Uploaded Successfully!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
        }
      })
      .catch(() => {
        toast.error("Something went wrong");
      });
  };

  useEffect(() => {
    fetchClients();
    fetchInstruments();
  }, []);

  useEffect(() => {
    if (params.id > 0) fetchExternalCalibration();
  }, [params.id]);

  useEffect(() => {
    if (inputStates?.clientId) fetchAddressesArray(inputStates.clientId);
  }, [inputStates?.clientId]);

  return (
    <>
      <Paper sx={{ mt: -2, p: 2 }}>
        <Typography variant="h6" component="h6" style={{ float: "left" }}>
          {params.id == 0
            ? "Create External Calibration"
            : "Edit External Calibration"}
        </Typography>

        <Grid container spacing={2} style={{ marginBottom: "10px" }}>
          {/* Client Name */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              size="small"
              id="autocomplete-clientId"
              options={clientsArray}
              getOptionLabel={(option) =>
                option.uniCode
                  ? `${option.id}, ${option.companyName} (${option.uniCode})`
                  : option.companyName || ""
              }
              isOptionEqualToValue={(option, value) =>
                option.id === value.id
              }
              value={
                clientsArray.find((item) => item.id === inputStates.clientId) || null
              }
              renderInput={(params) => (<TextField {...params} label="Client Name *" />)}
              onChange={(event, value) =>
                updateInputObject("clientId", value ? value.id : "")
              }
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              size="small"
              id="autocomplete-address"
              options={addressesArray}
              getOptionLabel={(option) => option.address || ""}
              value={
                addressesArray.find((item) => item.id === inputStates.address) ||
                null
              }
              renderInput={(p) => <TextField {...p} label="Address *" />}
              onChange={(event, value) =>
                updateInputObject("address", value ? value.id : "")
              }
            />
          </Grid>

          {/* Calibration Type */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              size="small"
              id="autocomplete-calibrationType"
              options={calibrationTypeOptions}
              getOptionLabel={(option) => option.name || ""}
              value={
                calibrationTypeOptions.find(
                  (item) => item.id === inputStates.calibrationType
                ) || null
              }
              renderInput={(p) => <TextField {...p} label="Calibration Type" />}
              onChange={(event, value) =>
                updateInputObject("calibrationType", value ? value.id : "")
              }
            />
          </Grid>

          {/* Entry Date */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                slotProps={{ textField: { size: "small", fullWidth: true } }}
                label="Entry Date "
                value={
                  inputStates.entryDate ? new Date(inputStates.entryDate) : null
                }
                format="dd/MM/yyyy"
                onChange={(date) => updateInputObject("entryDate", date)}
              />
            </LocalizationProvider>
          </Grid>

          {/* Service Request Number */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id="text-field-serviceReqNumber"
              label="Service Request Number"
              size="small"
              fullWidth
              variant="outlined"
              value={inputStates.serviceReqNumber || ""}
              onChange={(e) =>
                updateInputObject("serviceReqNumber", e.target.value)
              }
            />
          </Grid>

          {/* Instrument */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              size="small"
              id="autocomplete-instrumentId"
              options={instrumentsArray}
              getOptionLabel={(option) => `${option.id}, ${option.instrumentName}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={
                instrumentsArray.find((item) => item.id === inputStates.instrumentId) ||
                null
              }
              renderInput={(p) => <TextField {...p} label="Instrument *" />}
              onChange={(event, value) =>
                updateInputObject("instrumentId", value ? value.id : "")
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id="text-field-ducId"
              label="DUC ID"
              size="small"
              fullWidth
              variant="outlined"
              value={inputStates.ducId || ""}
              onChange={(e) =>
                updateInputObject("ducId", e.target.value)
              }
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id="text-field-model"
              label="Model"
              size="small"
              fullWidth
              variant="outlined"
              value={inputStates.model || ""}
              onChange={(e) =>
                updateInputObject("model", e.target.value)
              }
            />
          </Grid>
          
          
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id="text-field-make"
              label="Make"
              size="small"
              fullWidth
              variant="outlined"
              value={inputStates.make || ""}
              onChange={(e) =>
                updateInputObject("make", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id="text-field-serialNumber"
              label="Serial Number"
              size="small"
              fullWidth
              variant="outlined"
              value={inputStates.serialNumber || ""}
              onChange={(e) =>
                updateInputObject("serialNumber", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} sm={12} md={8} lg={3}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {modalConfigs.map((config) => (
                <MultiValueInputModal
                  key={config.transformedStringLabel}
                  buttonLabel={config.buttonLabel}
                  transformedStringLabel={config.transformedStringLabel}
                  inputStates={inputStates}
                  updateInputObject={updateInputObject}
                />
              ))}
            </div>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                slotProps={{ textField: { size: "small", fullWidth: true } }}
                label="Calibration Date *"
                value={
                  inputStates.calibrationDate ? new Date(inputStates.calibrationDate) : null
                }
                format="dd/MM/yyyy"
                onChange={(date) => updateInputObject("calibrationDate", date)}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                slotProps={{ textField: { size: "small", fullWidth: true } }}
                label="Due Date *"
                value={
                  inputStates.dueDate ? new Date(inputStates.dueDate) : null
                }
                format="dd/MM/yyyy"
                onChange={(date) => updateInputObject("dueDate", date)}
                minDate={new Date(inputStates.calibrationDate)}
              />
            </LocalizationProvider>
          </Grid>


          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id="text-field-certificateNumber"
              label="Certificate Number *"
              size="small"
              fullWidth
              variant="outlined"
              value={inputStates.certificateNumber || ""}
              onChange={(e) =>
                updateInputObject("certificateNumber", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id="text-field-ULRNo"
              label="ULR No"
              size="small"
              fullWidth
              variant="outlined"
              value={inputStates.ULRNo || ""}
              onChange={(e) =>
                updateInputObject("ULRNo", e.target.value)
              }
            />
          </Grid>

          
          {/* Certificate Upload */}
          <Grid item xs={12} sm={6} md={4} lg={3}
            style={{ textAlign: "left" }}
          >
            <Input
              id="file-upload-input-certificateFile"
              type="file"
              style={{ display: "none" }}
              onChange={(e) => handleFileUpload(e, "certificateFile")}
            />
            <label htmlFor="file-upload-input-certificateFile">
              <Button variant="contained" component="span">
                Upload Certificate Upload
              </Button>
            </label>
            <Typography variant="body2" color="textSecondary">
              Select a file to upload Certificate Upload
            </Typography>
          </Grid>
        </Grid>

        <br/>

        <Toolbar
          style={{
            padding: "0px",
            overflow: "auto",
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <Button
            variant="contained"
            style={{ backgroundColor: "grey" }}
            size="small"
            onClick={() => {
              if (window.confirm("Are you sure you want to cancel?")) {
                window.history.back();
              }
            }}
          >
            Cancel
          </Button>
          <Tooltip title={!(editAccess?.includes(1) || editAccess?.includes(0)) ? "You don't have access" : ''}>
            <Button
              variant="contained"
              size="small"
              disabled={!(editAccess?.includes(1) || editAccess?.includes(0))}
              sx={{ m: 0 }}
              onClick={handleSubmit}
            >
              {params.id > 0 ? "Update" : "Save"}
            </Button>
          </Tooltip>
        </Toolbar>
      </Paper>
    </>
  );
};

export default EditExternalCalibration;